"use client";

import { format, parseISO } from "date-fns";
import { useEffect, type ReactNode } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BackLink } from "@/components/shared/back-link";
import { BirthDateField } from "@/components/onboarding/birth-date-field";
import { ArrowRightIcon, Emphasis, STEP_CTA_CLASS, StepHeader } from "@/components/shared/step-header";
import { FieldError, FieldHelp } from "@/components/shared/field";
import { NOVA_LISTA_COPY } from "@/lib/admin/nova-lista/nova-lista-copy";
import { staffCustomerDataSchema, type StaffCustomerDataValues } from "@/lib/admin/nova-lista/nova-lista-schemas";
import { maskCpf } from "@/lib/cpf";
import { onlyDigits } from "@/lib/strings";
import { maskPhone } from "@/lib/onboarding/masks";
import {
  GENDER_LABELS,
  onboardingSignupSchema,
  type Gender,
  type OnboardingSignupValues,
} from "@/lib/onboarding/onboarding-schemas";

/** The prototype's order for the gender options (`GENDERS`, `onboarding-auth-flow.jsx`). */
const GENDER_OPTION_ORDER: readonly Gender[] = ["FEMALE", "MALE", "UNDISCLOSED", "NON_BINARY"];

/** The step's words that another flow may replace; the `lede` has its own prop. */
export interface SignupStepCopy {
  backLabel?: string;
  eyebrow?: string;
  title?: ReactNode;
  submitLabel?: string;
  pendingLabel?: string;
}

/** The host's words for the signup step. */
const DEFAULT_SIGNUP_COPY = {
  backLabel: "voltar",
  eyebrow: "Cadastro · seus dados",
  title: (
    <>
      Vamos criar o seu <Emphasis>cadastro</Emphasis>
    </>
  ),
  submitLabel: "Continuar para o endereço",
  pendingLabel: "Criando cadastro...",
} as const satisfies Required<SignupStepCopy>;

/** Placeholders per variant: the host speaks to the person, the staff about the customer. */
const PLACEHOLDERS = {
  host: {
    firstName: "Seu primeiro nome",
    lastName: "O restante do seu nome",
    email: "Digite o seu e-mail",
  },
  staff: NOVA_LISTA_COPY.data.placeholders,
} as const;

interface SignupStepFormBaseProps {
  cpf: string;
  isPending: boolean;
  errorMessage: string | null;
  /** Field-level error for email (e.g. "already in use"). */
  emailError?: string | null;
  onBack: () => void;
  /** What this door says under the heading — see `ONBOARDING_VOICE`. */
  lede: string;
  /** Called when the user edits the email field — clears the external email error. */
  onEmailChange?: () => void;
  /**
   * Locks the e-mail as `readOnly` (not `disabled`, so it is still submitted). Defaults to `false`.
   * Only an e-mail that arrives filled in `initialValues` is locked: an empty one stays editable.
   */
  emailReadOnly?: boolean;
  /** Help under the e-mail while it is locked by `emailReadOnly`. Defaults to none. */
  emailHelp?: string;
  /** Overrides the step's words; every field left out keeps the host's (`DEFAULT_SIGNUP_COPY`). */
  copy?: SignupStepCopy;
}

/** The host's signup (the default): every field, `onboardingSignupSchema`. */
interface HostSignupStepFormProps extends SignupStepFormBaseProps {
  variant?: "host";
  initialValues?: OnboardingSignupValues | null;
  onSubmit: (values: OnboardingSignupValues) => void;
}

/**
 * The staff's customer data: name, surname, mobile and e-mail with `staffCustomerDataSchema`;
 * no birth date, gender or marketing opt-in.
 */
interface StaffSignupStepFormProps extends SignupStepFormBaseProps {
  variant: "staff";
  initialValues?: StaffCustomerDataValues | null;
  onSubmit: (values: StaffCustomerDataValues) => void;
}

type SignupStepFormProps = HostSignupStepFormProps | StaffSignupStepFormProps;

const resolveStaffContactFields = zodResolver(staffCustomerDataSchema);

/**
 * The staff variant's resolver. Birth date, gender and opt-in are not rendered, so only the contact
 * fields go through `staffCustomerDataSchema`; on success its output (the phone as digits) replaces
 * what was typed, and the submit keeps only those four fields. `names` is dropped: the staff schema
 * has no host-only field names, and for a flat form the zod resolver nests errors the same without it.
 */
const resolveStaffSignup: Resolver<OnboardingSignupValues> = async (values, context, options) => {
  const result = await resolveStaffContactFields(values, context, { ...options, names: undefined });
  if (Object.keys(result.errors).length > 0) return { values: {}, errors: result.errors };
  return { values: { ...values, ...result.values }, errors: {} };
};

/** Hands the staff's handler the four contact fields the staff variant validated. */
function toStaffSubmit(onSubmit: (values: StaffCustomerDataValues) => void) {
  return ({ firstName, lastName, phone, email }: OnboardingSignupValues): void => onSubmit({ firstName, lastName, phone, email });
}

export function SignupStepForm(props: SignupStepFormProps) {
  const { cpf, isPending, errorMessage, emailError, initialValues, lede, onBack, onEmailChange, emailReadOnly = false, emailHelp, copy } = props;
  const isStaff = props.variant === "staff";
  const placeholders = isStaff ? PLACEHOLDERS.staff : PLACEHOLDERS.host;
  // The host-only fields read from here; a staff `initialValues` simply has none of them.
  const initial: Partial<OnboardingSignupValues> | null | undefined = initialValues;
  const isEmailLocked = emailReadOnly && (initial?.email ?? "").trim() !== "";
  const emailCaption = isEmailLocked ? emailHelp : undefined;
  const backLabel = copy?.backLabel ?? DEFAULT_SIGNUP_COPY.backLabel;
  const eyebrow = copy?.eyebrow ?? DEFAULT_SIGNUP_COPY.eyebrow;
  const title = copy?.title ?? DEFAULT_SIGNUP_COPY.title;
  const submitLabel = copy?.submitLabel ?? DEFAULT_SIGNUP_COPY.submitLabel;
  const pendingLabel = copy?.pendingLabel ?? DEFAULT_SIGNUP_COPY.pendingLabel;
  const {
    register,
    control,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<OnboardingSignupValues>({
    resolver: isStaff ? resolveStaffSignup : zodResolver(onboardingSignupSchema),
    // A field speaks once it is left, as the prototype's `touched` does — not only on submit.
    mode: "onTouched",
    // `phone` needs an explicit default so zod's type check doesn't fire before the friendly `.refine()` message.
    defaultValues: {
      firstName: initial?.firstName ?? "",
      lastName: initial?.lastName ?? "",
      phone: initial?.phone ?? "",
      birthDate: initial?.birthDate ?? "",
      gender: initial?.gender,
      marketingOptIn: initial?.marketingOptIn ?? true,
      email: initial?.email ?? "",
    },
  });

  // The host's handler goes to `handleSubmit` untouched, as before; the staff's gets only the contact fields.
  const submitValues = props.variant === "staff" ? toStaffSubmit(props.onSubmit) : props.onSubmit;

  // The step opens on its first field, as the prototype does — or on the e-mail when the step
  // came back to say it is taken. Mount only: the cursor is not moved while the person types.
  const initialFocus = emailError ? "email" : "firstName";
  useEffect(() => {
    setFocus(initialFocus);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form data-clarity-mask="true" onSubmit={handleSubmit(submitValues)} className="flex w-full flex-col">
      <BackLink onClick={onBack}>{backLabel}</BackLink>
      <StepHeader eyebrow={eyebrow} title={title} lede={lede} />

      <p className="mt-[-20px] mb-7 text-[13px] leading-[1.55] text-fg-muted">
        Todos os campos são obrigatórios, exceto os marcados como <span className="font-semibold text-foreground">opcional</span>.
      </p>

      <div className="mb-4 flex flex-col gap-2">
        <Label htmlFor="cpf-readonly">CPF</Label>
        <Input
          id="cpf-readonly"
          value={maskCpf(cpf)}
          readOnly
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          className="cursor-default text-fg-muted"
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 [@media(min-width:561px)]:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-2">
            <Label htmlFor="firstName">Nome</Label>
            <Input id="firstName" {...register("firstName")} autoComplete="given-name" placeholder={placeholders.firstName} aria-invalid={!!errors.firstName} aria-describedby="firstName-error" />
            <FieldError id="firstName-error">{errors.firstName?.message}</FieldError>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <Label htmlFor="lastName">Sobrenome</Label>
            <Input id="lastName" {...register("lastName")} autoComplete="family-name" placeholder={placeholders.lastName} aria-invalid={!!errors.lastName} aria-describedby="lastName-error" />
            <FieldError id="lastName-error">{errors.lastName?.message}</FieldError>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 [@media(min-width:561px)]:grid-cols-2">
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <div className="flex min-w-0 flex-col gap-2">
                <Label htmlFor="phone">Celular · WhatsApp</Label>
                <Input
                  id="phone"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="(XX) XXXXX-XXXX"
                  // field.value is always clean digits, masked only for
                  // display — so "voltar" shows it formatted, not raw.
                  value={maskPhone(field.value ?? "")}
                  aria-invalid={!!errors.phone}
                  aria-describedby="phone-error"
                  onChange={(event) => field.onChange(onlyDigits(event.target.value).slice(0, 11))}
                />
                <FieldError id="phone-error">{errors.phone?.message}</FieldError>
              </div>
            )}
          />
          {!isStaff && (
            <Controller
              control={control}
              name="birthDate"
              render={({ field }) => (
                <div className="flex min-w-0 flex-col gap-2">
                  <Label htmlFor="birthDate">Data de nascimento</Label>
                  <BirthDateField
                    id="birthDate"
                    value={field.value ? parseISO(field.value) : undefined}
                    onValueChange={(date) => field.onChange(format(date, "yyyy-MM-dd"))}
                    onClear={() => field.onChange("")}
                    onBlur={field.onBlur}
                    invalid={!!errors.birthDate}
                  />
                  <FieldError id="birthDate-error">{errors.birthDate?.message}</FieldError>
                </div>
              )}
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            type="email"
            {...register("email", { onChange: () => onEmailChange?.() })}
            autoComplete="email"
            placeholder={placeholders.email}
            // `readOnly`, not `disabled`: a disabled input would leave the e-mail out of the submit.
            readOnly={isEmailLocked || undefined}
            className={isEmailLocked ? "cursor-default text-fg-muted" : undefined}
            aria-invalid={!!errors.email || !!emailError}
            aria-describedby={emailCaption ? "email-help email-error" : "email-error"}
          />
          {emailCaption && <FieldHelp id="email-help">{emailCaption}</FieldHelp>}
          <FieldError id="email-error">{errors.email?.message || emailError}</FieldError>
        </div>

        {!isStaff && (
          <>
            <div className="flex flex-col gap-2">
              <Label htmlFor="gender">Gênero</Label>
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <DropdownMenu>
                    <DropdownMenuTrigger id="gender" aria-invalid={!!errors.gender} aria-describedby="gender-error">
                      <span className={field.value ? "" : "text-fg-subtle"}>
                        {field.value ? GENDER_LABELS[field.value] : "Selecione"}
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuRadioGroup value={field.value} onValueChange={field.onChange}>
                        {GENDER_OPTION_ORDER.map((gender) => (
                          <DropdownMenuRadioItem key={gender} value={gender}>
                            {GENDER_LABELS[gender]}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              />
              <FieldError id="gender-error">{errors.gender?.message}</FieldError>
            </div>

            <Controller
              control={control}
              name="marketingOptIn"
              render={({ field }) => (
                <label className="flex cursor-pointer items-start gap-2.5 text-[15px] leading-[1.55] text-foreground">
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-px shrink-0" />
                  Quero receber e-mails com promoções da Tania Bulhões.
                </label>
              )}
            />
          </>
        )}

        <FieldError>{errorMessage}</FieldError>
      </div>

      <Button type="submit" size="lg" disabled={isPending || !!emailError} className={`mt-7 w-full gap-2.5 ${STEP_CTA_CLASS}`}>
        {isPending ? pendingLabel : submitLabel}
        <ArrowRightIcon />
      </Button>
    </form>
  );
}
